import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { toastr } from "react-redux-toastr";
import { checkPasswordLink, setPassword, logout } from "../../actions/auth";
import { fieldClass, labelClass, primaryButton } from "../template/templateLabels";

const PASSWORD_MIN = 8;

// Opened from the link mailed to a new user, or to one whose password an admin
// reset (the backend's utils/passwordLink.js), as /set-password?t=<token>. They
// choose their own password here; the link works once, until it expires. Then
// they log in with it.
const SetPassword = ({ checkPasswordLink, setPassword, logout }) => {
  const token = new URLSearchParams(useLocation().search).get("t") || "";
  const history = useHistory();
  // { name, email, pending } while the link works
  const [link, setLink] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [password, setPasswordValue] = useState("");
  const [repeat, setRepeat] = useState("");
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    checkPasswordLink(token).then((result) => {
      if (!active) return;
      if (result.link) setLink(result.link);
      else setLinkError(result.errors.map((error) => error.msg).join(" "));
    });
    return () => {
      active = false;
    };
  }, [checkPasswordLink, token]);

  const passwordValid = password.length >= PASSWORD_MIN;
  const repeatValid = repeat === password;
  const canSubmit = !submitting && passwordValid && repeatValid;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrors([]);
    const result = await setPassword(token, password);
    if (result.email) {
      // Whoever was logged in on this browser logs in again, as this user
      logout();
      toastr.success("Нууц үг хадгалагдлаа. Шинэ нууц үгээрээ нэвтэрнэ үү.");
      history.replace("/");
    } else {
      setErrors(result.errors.map((error) => error.msg));
      setSubmitting(false);
    }
  };

  const hint = (invalid) =>
    "block mt-1 text-xs " +
    (invalid ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400");

  let body;
  if (linkError) {
    body = (
      <div className="text-center">
        <p className="text-sm text-gray-600 dark:text-gray-300">{linkError}</p>
        <Link
          to="/"
          className="inline-block mt-4 text-sm font-medium text-purple-600 hover:underline dark:text-purple-400"
        >
          Нэвтрэх хуудас руу буцах
        </Link>
      </div>
    );
  } else if (!link) {
    body = (
      <p className="text-sm text-center text-gray-500 dark:text-gray-400">
        Холбоосыг шалгаж байна...
      </p>
    );
  } else {
    body = (
      <form onSubmit={submit} className="space-y-4 text-left">
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{link.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{link.email}</p>
        </div>

        {errors.length > 0 && (
          <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md dark:bg-red-900 dark:text-red-100">
            {errors.map((message, i) => (
              <p key={i}>{message}</p>
            ))}
          </div>
        )}

        {/* Lets a password manager save the password for this email */}
        <input type="email" value={link.email} autoComplete="username" readOnly hidden />

        <label className="block">
          <span className={labelClass}>Шинэ нууц үг</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPasswordValue(e.target.value)}
            disabled={submitting}
            autoComplete="new-password"
            autoFocus
            className={fieldClass}
          />
          <span className={hint(password !== "" && !passwordValid)}>
            Хамгийн багадаа {PASSWORD_MIN} тэмдэгт.
          </span>
        </label>

        <label className="block">
          <span className={labelClass}>Нууц үг давтах</span>
          <input
            type="password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            disabled={submitting}
            autoComplete="new-password"
            className={fieldClass}
          />
          {repeat !== "" && !repeatValid && (
            <span className={hint(true)}>Нууц үг таарахгүй байна.</span>
          )}
        </label>

        <button
          type="submit"
          disabled={!canSubmit}
          className={
            primaryButton + " w-full" + (canSubmit ? "" : " opacity-50 cursor-not-allowed")
          }
        >
          {submitting ? "Хадгалж байна..." : "Нууц үг хадгалах"}
        </button>
      </form>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full mx-4 my-10 md:mt-40">
        <div className="w-full max-w-sm mx-auto overflow-hidden bg-white rounded-lg shadow-md dark:bg-gray-800">
          <div className="py-2 bg-purple-600" />
          <div className="px-6 py-5">
            <h2 className="text-2xl font-bold text-center text-gray-700 dark:text-white">
              CloudPrint
            </h2>
            <p className="mt-1 mb-5 text-sm text-center text-gray-500 dark:text-gray-400">
              {link && !link.pending ? "Нууц үг сэргээх" : "Бүртгэл баталгаажуулах"}
            </p>
            {body}
          </div>
          <div className="py-2 bg-purple-600" />
        </div>
      </div>
    </div>
  );
};

SetPassword.propTypes = {
  checkPasswordLink: PropTypes.func.isRequired,
  setPassword: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
};

export default connect(null, { checkPasswordLink, setPassword, logout })(SetPassword);
